import { useContext, useId, type ReactNode } from "react";
import { Chart, type ChartData, type ChartSeries } from "../Chart.tsx";
import { TimeRangeContext } from "../../state/time-range.tsx";

/**
 * MetricChartCard — a dense, Netdata-adapted chart panel (observability-ui Netdata-adaptation).
 *
 * This is the denser sibling of {@link ChartCard}: the same titled panel over the shared `Chart`
 * wrapper, but with the two chrome elements that most define the Netdata look —
 *   - a compact header: the title, a muted METRIC-PATH subtitle (e.g. "oathera.gateway.decision ·
 *     per bucket"), a live/paused marker, and an optional toolbar slot (top-right); and
 *   - a BOTTOM DIMENSION-LEGEND STRIP: one chip per plotted series showing a colour SWATCH + the
 *     dimension name + its CURRENT value, exactly like Netdata's under-chart legend.
 *
 * It is a theme-aware ADAPTATION, not a clone: every colour (swatches included) resolves from the
 * theme tokens via `observability.css`, so it reads correctly in light AND dark with no permanently
 * dark surface. The swatch colour for a series comes from that series' `colorVar` (the same token
 * the line is drawn with), so the legend and the plot always agree.
 *
 * Honesty (design 12.3): the dimension values come straight from the data the caller already passes
 * to the chart — the last real point of each series — so nothing is fabricated; a series with no
 * points shows an em-dash, and the whole card falls back to the Chart wrapper's honest empty state.
 */

/** One legend dimension: derived from a series + the data, rendered as swatch + name + value. */
export interface MetricDimension {
  readonly label: string;
  readonly colorVar: string;
  /** The current (latest) value as display text; already formatted by the caller. */
  readonly value: string;
}

export interface MetricChartCardProps {
  /** Panel title; also the chart's accessible name and the region label. */
  readonly title: string;
  /** Muted metric-path/context subtitle shown under the title (e.g. "system.cpu · percent"). */
  readonly metricPath?: string;
  /** Aligned chart data `[xs, ...ys]`, forwarded to the Chart wrapper. */
  readonly data: ChartData;
  /** One descriptor per plotted y column, forwarded to the Chart wrapper. */
  readonly series: readonly ChartSeries[];
  /** Chart height in CSS px; width tracks the card. */
  readonly height?: number;
  /** Show the chart's polite loading status instead of the plot. */
  readonly loading?: boolean;
  /** Override the chart's empty-state cause when the window has no data. */
  readonly empty?: string;
  /** Format a dimension's latest value for the legend strip. Defaults to a grouped number. */
  readonly formatValue?: (value: number) => string;
  /** Optional toolbar content rendered top-right (e.g. expand / group-by affordances). */
  readonly toolbar?: ReactNode;
  /** Hide the bottom legend strip (e.g. a single-series spark where it adds nothing). */
  readonly hideLegend?: boolean;
}

export function MetricChartCard(props: MetricChartCardProps): ReactNode {
  const headingId = useId();
  const timeRange = useContext(TimeRangeContext);
  const liveState = timeRange === null ? null : timeRange.live ? "live" : "paused";

  const dims = props.hideLegend === true ? [] : buildDimensions(props);

  return (
    <section className="obs-panel obs-metric-card" role="region" aria-labelledby={headingId}>
      <header className="obs-metric-card__header">
        <div className="obs-metric-card__titles">
          <h3 className="obs-panel__title obs-metric-card__title" id={headingId}>
            {props.title}
          </h3>
          {props.metricPath !== undefined && props.metricPath !== "" && (
            <span className="obs-metric-card__path">{props.metricPath}</span>
          )}
        </div>
        <div className="obs-metric-card__tools">
          {liveState !== null && (
            <span
              className="obs-metric-card__live"
              data-state={liveState}
              role="status"
              aria-label={liveState === "live" ? "Live: tracking now" : "Paused: window frozen"}
            >
              {liveState === "live" ? "Live" : "Paused"}
            </span>
          )}
          {props.toolbar}
        </div>
      </header>

      <div className="obs-metric-card__body">
        <Chart
          title={props.title}
          data={props.data}
          series={props.series}
          {...(props.loading !== undefined ? { loading: props.loading } : {})}
          {...(props.empty !== undefined ? { emptyCause: props.empty } : {})}
          {...(props.height !== undefined ? { height: props.height } : {})}
          {...(props.formatValue !== undefined ? { formatY: props.formatValue } : {})}
        />
      </div>

      {dims.length > 0 && (
        <ul className="obs-metric-card__legend" aria-label="Dimensions">
          {dims.map((d) => (
            <li key={d.label} className="obs-metric-card__dim">
              {/* The swatch colour is set from the series' own theme token via a CSS custom property,
                  so the legend matches the plotted line and still follows the active theme. */}
              <span
                className="obs-metric-card__swatch"
                style={{ ["--dim-color" as string]: `var(${d.colorVar})` }}
                aria-hidden="true"
              />
              <span className="obs-metric-card__dim-label">{d.label}</span>
              <span className="obs-metric-card__dim-value">{d.value}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Derive the legend dimensions from the chart data: for each series (y column i), take the LAST
 * finite point of `data[i+1]` as its current value. No fabrication — a series with no finite point
 * reads as an em-dash.
 */
function buildDimensions(props: MetricChartCardProps): readonly MetricDimension[] {
  const fmt = props.formatValue ?? ((v: number) => v.toLocaleString());
  const out: MetricDimension[] = [];
  props.series.forEach((s, i) => {
    const col = props.data[i + 1];
    let current = "—";
    if (col !== undefined) {
      for (let j = col.length - 1; j >= 0; j--) {
        const v = col[j];
        if (v !== undefined && Number.isFinite(v)) {
          current = fmt(v);
          break;
        }
      }
    }
    out.push({ label: s.label, colorVar: s.colorVar ?? "--accent", value: current });
  });
  return out;
}
